import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { ReadingBookmarkRow } from "@/types/db";

export interface ReadingBookmark {
  id: string;
  slug: string;
  title: string;
  category: string | null;
  hero_image: string | null;
  kind: string;
  saved_at: string;
}

export interface BookmarkInput {
  slug: string;
  title: string;
  category?: string;
  hero_image?: string;
  kind?: string;
}

export function useReadingList() {
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState<ReadingBookmark[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!user) {
      setBookmarks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("reading_bookmarks")
      .select("*")
      .eq("user_id", user.id)
      .order("saved_at", { ascending: false });
    setBookmarks(((data as ReadingBookmarkRow[]) ?? []) as ReadingBookmark[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const isSaved = useCallback(
    (slug: string) => bookmarks.some((b) => b.slug === slug),
    [bookmarks]
  );

  const add = useCallback(
    async (input: BookmarkInput) => {
      if (!user) return { error: new Error("Not signed in") };
      const { error } = await supabase.from("reading_bookmarks").insert({
        user_id: user.id,
        slug: input.slug,
        title: input.title,
        category: input.category ?? null,
        hero_image: input.hero_image ?? null,
        kind: input.kind ?? "article",
      });
      if (!error) await fetchAll();
      return { error };
    },
    [user, fetchAll]
  );

  const remove = useCallback(
    async (slug: string) => {
      if (!user) return;
      await supabase
        .from("reading_bookmarks")
        .delete()
        .eq("user_id", user.id)
        .eq("slug", slug);
      await fetchAll();
    },
    [user, fetchAll]
  );

  const toggle = useCallback(
    async (input: BookmarkInput) => {
      if (isSaved(input.slug)) {
        await remove(input.slug);
        return false;
      }
      await add(input);
      return true;
    },
    [isSaved, add, remove]
  );

  return { bookmarks, loading, isSaved, add, remove, toggle, refetch: fetchAll };
}
